/**
 * manager-search.js
 * Gerencia busca preditiva com dois modos de UI.
 * Disponível como window.SearchManager.
 *
 * ─── Modo drawer ──────────────────────────────────────────────────────────
 *
 *   O SearchManager fica DENTRO do elemento data-drawer="search".
 *   A abertura/fechamento é controlada pelo DrawerManager.
 *
 *   <!-- section search-drawer.liquid -->
 *   <div data-drawer="search" data-drawer-side="right">
 *     <div data-drawer-overlay></div>
 *     <div data-search data-search-mode="drawer">
 *       <input data-search-input type="search">
 *       <button data-search-clear hidden>×</button>
 *       <div data-search-pre-search>...</div>
 *       <div data-search-results></div>
 *     </div>
 *   </div>
 *
 * ─── Modo dropdown ────────────────────────────────────────────────────────
 *
 *   O SearchManager fica no header. O painel de resultados abre abaixo do input.
 *
 *   <div data-search data-search-mode="dropdown">
 *     <input data-search-input type="search">
 *     <button data-search-clear hidden>×</button>
 *     <div data-search-dropdown hidden>
 *       <div data-search-pre-search>...</div>
 *       <div data-search-results></div>
 *     </div>
 *   </div>
 *
 * ─── Data attributes ──────────────────────────────────────────────────────
 *
 *   data-search                → container raiz
 *   data-search-mode           → "drawer" | "dropdown"
 *   data-search-input          → campo de busca
 *   data-search-results        → container de resultados
 *   data-search-pre-search     → estado pré-busca (visível quando input vazio)
 *   data-search-clear          → botão limpar input
 *   data-search-dropdown       → painel dropdown (modo dropdown apenas)
 *   data-search-result         → item de resultado (gerado pelo manager)
 *
 * ─── API pública ──────────────────────────────────────────────────────────
 *
 *   window.SearchManager.open()
 *   window.SearchManager.close()
 *   window.SearchManager.toggle()
 *   window.SearchManager.destroy()
 */

class SearchManager {
  constructor() {
    this._container = document.querySelector('[data-search]');
    if (!this._container) return;

    this._mode       = this._container.dataset.searchMode || 'dropdown';
    this._input      = this._container.querySelector('[data-search-input]');
    this._results    = this._container.querySelector('[data-search-results]');
    this._preSearch  = this._container.querySelector('[data-search-pre-search]');
    this._clearBtn   = this._container.querySelector('[data-search-clear]');
    this._dropdown   = this._container.querySelector('[data-search-dropdown]');

    this._debounceTimer = null;
    this._abortController = null;
    this._DEBOUNCE_MS   = 300;
    this._MIN_CHARS     = 2;
    this._RESULTS_LIMIT = 6;

    this._onInput   = this._onInput.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onClear   = this._onClear.bind(this);

    this._init();
  }

  // ─── Inicialização ────────────────────────────────────────────────────────

  _init() {
    if (!this._input) return;

    this._input.addEventListener('input',   this._onInput);
    this._input.addEventListener('keydown', this._onKeyDown);
    this._clearBtn?.addEventListener('click', this._onClear);

    if (this._mode === 'dropdown') {
      this._initDropdown();
    } else {
      this._initDrawer();
    }
  }

  // ─── Modo Dropdown ────────────────────────────────────────────────────────

  _initDropdown() {
    // Abre ao focar no input
    this._input.addEventListener('focus', () => {
      this._showDropdown();
      this._showPreSearch();
    });

    // Fecha ao clicar fora do container
    document.addEventListener('click', e => {
      if (!this._container.contains(e.target)) this._hideDropdown();
    });

    // Fecha ao abrir qualquer drawer
    window.eventBus?.on('drawer:open', () => this._hideDropdown());
  }

  _showDropdown() {
    this._dropdown?.removeAttribute('hidden');
    this._dropdown?.classList.add('is-open');
    this._input.setAttribute('aria-expanded', 'true');
  }

  _hideDropdown() {
    this._dropdown?.setAttribute('hidden', '');
    this._dropdown?.classList.remove('is-open');
    this._input.setAttribute('aria-expanded', 'false');
  }

  // ─── Modo Drawer ──────────────────────────────────────────────────────────

  _initDrawer() {
    window.eventBus?.on('drawer:open', ({ name }) => {
      if (name !== 'search') return;
      // Foca no input apenas em desktop (evita teclado virtual no mobile)
      if (window.innerWidth >= 750) {
        requestAnimationFrame(() => this._input?.focus());
      }
      this._showPreSearch();
    });

    // Limpa resultados ao fechar o drawer
    window.eventBus?.on('drawer:close', ({ name }) => {
      if (name !== 'search') return;
      this._clearResults();
      this._removeSearchNotice();
      this._showPreSearch();
      this._toggleClear(false);
      if (this._input) this._input.value = '';
    });
  }

  // ─── Busca ────────────────────────────────────────────────────────────────

  _onInput() {
    const query = this._input.value.trim();
    clearTimeout(this._debounceTimer);
    this._toggleClear(query.length > 0);

    if (query.length < this._MIN_CHARS) {
      this._abortController?.abort();
      this._clearResults();
      this._removeSearchNotice();
      this._showPreSearch();
      return;
    }

    this._debounceTimer = setTimeout(() => this._fetchResults(query), this._DEBOUNCE_MS);
  }

  async _fetchResults(query) {
    this._abortController?.abort();
    const requestController = new AbortController();
    this._abortController = requestController;
    this._results?.setAttribute('aria-busy', 'true');

    try {
      const endpoint = this._container.dataset.predictiveSearchUrl || '/search/suggest';
      const url = `${endpoint}.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${this._RESULTS_LIMIT}&resources[options][unavailable_products]=last`;
      const res = await fetch(url, {
        signal: requestController.signal,
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const data     = await res.json();
      const products = data.resources?.results?.products || [];
      if (this._input.value.trim() !== query) return;
      this._renderResults(products, query);
    } catch (err) {
      if (err.name !== 'AbortError') console.error('[SearchManager] Fetch failed:', err);
    } finally {
      if (this._abortController === requestController) {
        this._results?.setAttribute('aria-busy', 'false');
      }
    }
  }

  // ─── Renderização ─────────────────────────────────────────────────────────

  _renderResults(products, query) {
    if (!this._results) return;

    this._clearResults();
    this._removeSearchNotice();

    if (products.length === 0) {
      const notice = document.createElement('p');
      notice.className = 'searchModal__notice';
      notice.setAttribute('role', 'status');
      notice.textContent = this._results.dataset.noResults || 'Nenhuma sugestão encontrada.';
      this._results.append(notice);
      this._input.setAttribute('aria-expanded', 'true');
      return;
    }

    this._hidePreSearch();
    const label = document.createElement('span');
    label.className = 'searchModal__resultsLabel';
    label.textContent = this._results.dataset.resultsLabel || 'Resultados sugeridos:';

    const list = document.createElement('ul');
    list.className = 'searchModal__resultsList';
    list.setAttribute('role', 'listbox');

    products.forEach(product => list.append(this._renderResult(product)));
    this._results.append(label, list);
    this._input.setAttribute('aria-expanded', 'true');

    // Navegação por teclado nos resultados
    const items = [...this._results.querySelectorAll('[data-search-result]')];
    items.forEach((el, i) => {
      el.addEventListener('keydown', e => {
        if (e.key === 'ArrowDown') { e.preventDefault(); items[i + 1]?.focus() ?? items[0]?.focus(); }
        if (e.key === 'ArrowUp')   { e.preventDefault(); i === 0 ? this._input.focus() : items[i - 1]?.focus(); }
        if (e.key === 'Escape')    { this._clearInput(); }
      });
    });
  }

  _renderResult(product) {
    const item = document.createElement('li');
    item.className = 'searchModal__resultItem';
    item.setAttribute('role', 'option');

    const link = document.createElement('a');
    link.className = 'searchModal__result';
    link.href = product.url;
    link.textContent = product.title;
    link.setAttribute('data-search-result', '');

    item.append(link);
    return item;
  }

  // ─── Estado de UI ─────────────────────────────────────────────────────────

  _onKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const first = this._results?.querySelector('[data-search-result]');
      first?.focus();
    }
    if (e.key === 'Escape') {
      this._clearInput();
      if (this._mode === 'dropdown') this._hideDropdown();
      else window.DrawerManager?.close('search');
    }
  }

  _onClear() {
    this._clearInput();
    this._input.focus();
  }

  _clearInput() {
    if (this._input) this._input.value = '';
    this._clearResults();
    this._removeSearchNotice();
    this._showPreSearch();
    this._toggleClear(false);
  }

  _clearResults() {
    if (this._results) {
      this._results.innerHTML = '';
      this._results.setAttribute('aria-busy', 'false');
    }
    this._input?.setAttribute('aria-expanded', 'false');
  }

  _removeSearchNotice() {
    this._preSearch?.querySelector('[data-search-notice]')?.remove();
  }

  _showPreSearch() {
    this._preSearch?.removeAttribute('hidden');
  }

  _hidePreSearch() {
    this._preSearch?.setAttribute('hidden', '');
  }

  _toggleClear(show) {
    if (!this._clearBtn) return;
    this._clearBtn.hidden = !show;
  }

  // ─── API pública ──────────────────────────────────────────────────────────

  open() {
    if (this._mode === 'drawer') window.DrawerManager?.open('search');
    else this._showDropdown();
  }

  close() {
    if (this._mode === 'drawer') window.DrawerManager?.close('search');
    else this._hideDropdown();
  }

  toggle() {
    if (this._mode === 'drawer') window.DrawerManager?.toggle('search');
    else this._dropdown?.classList.contains('is-open') ? this._hideDropdown() : this._showDropdown();
  }

  destroy() {
    this._input?.removeEventListener('input',   this._onInput);
    this._input?.removeEventListener('keydown', this._onKeyDown);
    clearTimeout(this._debounceTimer);
    this._abortController?.abort();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.SearchManager = new SearchManager();
});
